// Ionic Imports
import { Platform } from "@ionic/angular";

// Angular Imports
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router, NavigationEnd } from "@angular/router";
import { Injectable } from "@angular/core";

// Custom Services
import { PromptService } from "./prompt.service";
import { PanesService } from "./panes.service";

@Injectable({ providedIn: "root" })
export class NavigationService {

  // History of visited URLs
  private history: string[] = [];

  // Default href for the back button
  private defaultBackButtonHref: string = "/";

  private lastBackTime: number = 0;
  private intervalExitApp: number = 2000;

  /**
   * Constructor
   */
  constructor(
    private router: Router,
    private panes: PanesService,
    private platform: Platform,
    private promptService: PromptService,
  ) {
    this.router.events.pipe(
      takeUntilDestroyed()
    ).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const url = event.urlAfterRedirects;
        const index = this.history.lastIndexOf(url);

        if (index !== -1) {
          // If the new route already exists in the history array,
          // remove all subsequent entries and keep the existing one.
          this.history = this.history.slice(0, index + 1);
        } else if (this.history[this.history.length - 1] !== url) {
          // Otherwise, push the new entry to the history array.
          this.history.push(url);
        }
      }
    });
  } // End of constructor()

  /**
   * Check if the user can go back
   * @return {boolean}
   */
  canGoBack(): boolean {
    return this.history.length > 1;
  }

  /**
   * Get the previous URL from the history
   * @return {string}
   */
  getPreviousUrl(): string {
    // console.log(this.history);
    return this.history[this.history.length - 1] || this.defaultBackButtonHref;
  } // End of getPreviousUrl()

  /**
   * Navigate back to the previous URL
   * @return {void}
   */
  public back(): void {
    this.history.pop();
    const prevUrl = this.history.length > 0 ? this.history[this.history.length - 1] : this.defaultBackButtonHref;

    if (this.history.length > 0) {
      this.router.navigateByUrl(prevUrl!);
    } else {
      this.router.navigateByUrl(this.defaultBackButtonHref);
    }
  } // End of back()



  /**
   * Handle the hardware back button (click) event on mobile devices
   * @return {void}
   */
  public hardwareBackButtonEvent(): void {
    this.platform.backButton.subscribeWithPriority(10, () => {
      let currentTime = new Date().getTime();
      if (!this.canGoBack() && this.lastBackTime && this.lastBackTime > 0 && (currentTime - this.lastBackTime < this.intervalExitApp)) {
        (window.navigator as any)["app"].exitApp();
        return;
      }
      // If there is an open pane, close it
      const closedPanes = this.panes.closeAllPanes();
      console.log(closedPanes);
      if (closedPanes > 0) {
        return;
      }

      // if there is no previous page, show toast to exit app
      if (!this.canGoBack()) {
        this._createToastExitApp();
      } else {
        this.back();
      }
    });
  } // End of backButtonEvent()

  /**
   * Create a toast to exit the app
   * @return {void}
   */
  private _createToastExitApp(): void {
    this.promptService.showToast("Press 'Back' again to exit", { icon: 'chevron-back-outline' });
    this.lastBackTime = new Date().getTime();
  } // End of createToastExitApp()


  /**
   * Update the language in the history, so the user can go back to the previous page in the new language
   * @param {string} newLang - The new language code
   * @return {void}
   */
  public updateLanguageInHistory(newLang: string): void {
    this.history = this.history.map(url => {
      const segments = url.split('/');
      if (segments.length > 1) { // Assuming URLs have at least one segment after the initial '/'
        segments[1] = newLang; // Change the language segment
      }
      return segments.join('/');
    });
  } // End of updateLanguageInHistory()

} // End of NavigationService